import { GetStaticPropsContext } from 'next';

import { Box, Grid, Heading } from '@chakra-ui/react';

import { ButtonLink } from '~/components/button-link';
import { ContentContainer } from '~/components/container';
import { SanityImage } from '~/components/sanity-image';
import { DEFAULT_LANGUAGE } from '~/features/internationalization/languages';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { getPage } from '~/features/page/controllers/get-page';
import { Page } from '~/features/page/models/page';
import { getPageTemplate } from '~/features/page-template/controllers/get-page-template';
import {
  SchoolLibraryTemplate,
  schoolLibraryTemplateQuery,
  schoolLibraryTemplatesSchema,
} from '~/features/page-template/templates/school-library/model';
import { FooterBannerSection } from '~/features/seo/components/footer-banner-section';
import { ItemCard } from '~/features/seo/components/item-card';
import { WaveHeroSection } from '~/features/seo/components/wave-hero-section';

export default function SchoolsPage({
  page,
  schoolLibraryTemplate,
}: {
  page: Page;
  schoolLibraryTemplate: SchoolLibraryTemplate;
}) {
  return (
    <>
      <PageHead page={page} />
      <PageLayout withoutVerticalPadding>
        <WaveHeroSection description={schoolLibraryTemplate.pageSubtitle} title={schoolLibraryTemplate.pageTitle} />
        <Box
          as='section'
          bgColor='white'
          padding={{
            base: '40px 0px',
            md: '80px 0px',
          }}
        >
          <ContentContainer>
            <Heading
              as='h2'
              size='marketing-5xl'
              textAlign='center'
              mb={{
                base: 8,
                md: 12,
              }}
            >
              {schoolLibraryTemplate.schoolListTitle}
            </Heading>
            <Grid
              gap={{
                base: 8,
                md: 12,
              }}
              gridTemplateColumns={{
                md: 'repeat(auto-fill, minmax(341px, 1fr))',
              }}
            >
              {schoolLibraryTemplate?.schools?.map((school) => {
                return (
                  <ItemCard
                    key={school._id}
                    summary={school.externalDescription ?? school.description2 ?? school.description}
                    summaryLinesShown={8} // School descriptions are generally 5-6 lines long on desktop. Use 8 to hide all Expand buttons.
                    title={school.title}
                    buttonSlot={
                      <ButtonLink
                        buttonProps={{ colorScheme: 'blue', width: 'fit-content', variant: 'outline' }}
                        linkProps={{ href: `/school/${school.slug}` }}
                      >
                        See School
                      </ButtonLink>
                    }
                    imageSlot={
                      school.graphic2 && (
                        <SanityImage
                          alt=''
                          image={school.graphic2}
                          sizes='682px'
                          style={{
                            objectFit: 'cover',
                            pointerEvents: 'none',
                          }}
                          fill
                        />
                      )
                    }
                  />
                );
              })}
            </Grid>
          </ContentContainer>
        </Box>

        {schoolLibraryTemplate.footerBanner && (
          <FooterBannerSection
            ctaLabel={schoolLibraryTemplate.footerBanner.ctaLabel}
            ctaLink={schoolLibraryTemplate.footerBanner.ctaLink}
            subTitle={schoolLibraryTemplate.footerBanner.subTitle}
            theme={schoolLibraryTemplate.footerBanner.theme}
            title={schoolLibraryTemplate.footerBanner.title}
          />
        )}
      </PageLayout>
    </>
  );
}

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  // Stop additional locale pages except the default locale from being built
  if (locale !== DEFAULT_LANGUAGE) {
    return {
      notFound: true,
    };
  }

  const [page, schoolLibraryTemplate] = await Promise.all([
    getPage('/schools'),
    getPageTemplate<SchoolLibraryTemplate>({
      templateName: 'School Library Template',
      templateQuery: schoolLibraryTemplateQuery,
      templateZodSchema: schoolLibraryTemplatesSchema,
    }),
  ]);

  if (!page || !schoolLibraryTemplate) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      page,
      schoolLibraryTemplate,
    },
  };
}
